import { useTranslation } from 'react-i18next';
import { ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatInteger } from '@/lib/language';
import { SUPPORT_EMAIL } from '@/lib/plans';

export interface FeedCheckNextStepProps {
  /** Der gemessene Handlungsschritt: `useJourney().actionStep` (NICHT `nextStep`).
   *  null = nichts offen. */
  /**
   * `ads_connect` ist seit dem 16.09.2026 der fuenfte Schritt (Spec Abschnitt
   * 3.2). `ads_campaigns` steht hier bewusst NICHT: Schritt 6 ist ein Ausblick
   * ohne Handlung, seine Karte ist der `Done`-Zustand mit dem Teaser.
   */
  step: 'connect' | 'optimize' | 'approve' | 'upload' | 'ads_connect' | null;
  /** Die Alternative daneben: `useJourney().actionSecondaryStep`, heute nur 'approve'. */
  secondaryStep?: 'approve' | null;
  /** Gemessene Menge zum Primaerschritt (freizugeben bzw. zu uebertragen). */
  actionCount?: number | null;
  /** Menge zum Optimieren-Angebot (`resolveOptimizeTarget`). */
  optimizeCount: number;
  /** Menge zur Freigabe-Alternative (`stats.pendingReviewAwaitingUpload`). */
  secondaryCount?: number | null;
  /** Navigation fuer connect/approve. */
  onAction?: () => void;
  /** Optimierungslauf starten. */
  onRun?: () => void;
  /** Upload starten (nur im upload-Zweig; ohne die Prop bleibt der Knopf Navigation). */
  onUploadRun?: () => void;
  /** Ziel der Alternative (heute: Werkbank). */
  onSecondary?: () => void;
  /** Das Optimieren-Angebot ist zusaetzlich als Alternative erlaubt (offersOptimizeSecondary). */
  offersOptimizeSecondary?: boolean;
  loading?: boolean;
  uploadBusy?: boolean;
  mcAccessRevoked?: boolean;
  optimizationRunning?: boolean;
  /** Nichts zu optimieren: leerer Snapshot oder abgearbeiteter Bestand. */
  isEmpty?: boolean;
  /** Kontingent erschoepft (`useJourney().quotaBlocked` bei leerer Menge). */
  quotaExhausted?: boolean;
  /** Ziel bei erschoepftem Kontingent (/pricing). */
  onQuota?: () => void;
  /** `useJourney().autoUpload` -- schaltet die Vertrauenszeile auf die praezise Fassung. */
  autoUpload?: boolean;
  /** Ein Upload dieses Feeds laeuft, aber nicht der eigene. */
  uploadElsewhere?: { uploaded: number; total: number };
  /**
   * Schritt 5 ist erledigt (ein Ads-Konto ist verknuepft). Nur fuer die
   * Begruendung im `Done`-Zustand: dann stehen die Zahlen in Kapitel 3, und der
   * Satz sagt das statt "neue Produkte holt der naechtliche Sync".
   */
  adsLinked?: boolean;
  /** A2-NEU-1: Mitglied, das Ads-Konto verwaltet der Eigentuemer. */
  adsOwnerManaged?: boolean;
  /**
   * WP5 K22: am Google-Konto haengt ein Ads-Konto, es ist diesem Merchant
   * Center nur nicht zugeordnet. Dann heisst die Handlung "zuordnen", nicht
   * "verbinden"; das Ads-Kapitel sagt dasselbe.
   */
  adsAccountConnected?: boolean;
  /**
   * Die Nummer des Ads-Kapitels auf DIESER Seite. Sie ist keine Konstante:
   * `chapterTotal` haengt daran, ob ein Preis-Benchmark vorliegt, und ohne ihn
   * ist Ads Kapitel 2 von 3 -- der haeufigste Fall (Feed ohne GTIN-Treffer).
   * Stand die 3 in der Copy, sagte die Karte "Kapitel 3", waehrend die
   * Ueberschrift zwei Bildschirmhoehen tiefer "Kapitel 2 von 3" sagte. Fehlt
   * die Prop, nennt der Satz gar keine Nummer statt einer falschen.
   */
  adsChapterIndex?: number;
  /**
   * Der Teaser "Interesse melden" im `Done`-Zustand (Spec 3.2, D4). Bewusst
   * DIESELBE `useAdsInterest`-Instanz wie im Ads-Kapitel, von der Seite
   * hereingereicht: zwei Instanzen hiessen zwei `localStorage`-Marker fuer
   * dieselbe Meldung, und der Kunde koennte sie zweimal abgeben.
   */
  onInterest?: () => void;
  /** Die Meldung ist schon abgegeben -- der Knopf bleibt sichtbar, aber gesperrt. */
  interestSent?: boolean;
  /** Die Meldung wird gerade geschrieben. */
  interestBusy?: boolean;
  /**
   * WP5 A2O-11: Link vom `Done`-Zustand zum Ads-Kapitel. Nur mit
   * verknuepftem Konto gereicht (die Seite setzt sie an `adsLinked`).
   */
  onOpenAds?: () => void;
  /**
   * WP5 A2O-12: Kapazitaet voll, aber es gibt noch optimierbare Produkte --
   * das Angebot verschwand vorher kommentarlos. `kind` folgt der
   * WP1-Kaufflaechen-Einordnung (Ruling D4): `owner` bei einem Mitglied
   * einer fremden Merchant Center (das Kontingent gehoert dann dem
   * Eigentuemer, kein Knopf); `contact` bei Sondertarif oder ohne
   * groesseres Band (Kontaktweg statt Kauf); sonst `upgrade` (Knopf zu
   * `/pricing`).
   */
  capacityFull?: {
    count: number;
    kind: 'upgrade' | 'contact' | 'owner';
    /** Nur fuer `kind: 'owner'`: die Mail-Adresse des Eigentuemers. */
    ownerEmail?: string | null;
  } | null;
}

/**
 * Die vier Anzeigezustaende der Karte plus die zwei Sonderfaelle.
 *
 * Namensgleich mit den Locale-Suffixen (headingApprove, ctaApprove, ...),
 * damit die Ableitung hier keine eigene Benennung erfindet, die im JSON
 * wieder uebersetzt werden muesste.
 */
type Mode = 'Approve' | 'Upload' | 'Optimize' | 'Connect' | 'Done' | 'Quota' | 'AdsConnect';

function deriveMode(
  step: FeedCheckNextStepProps['step'],
  quotaExhausted: boolean,
  isEmpty: boolean,
): Mode {
  // Kontingent gewinnt vor allem anderen — ein Angebot, das der Server
  // ohnehin ablehnen wuerde, ist kein Angebot (Muster JourneyInput.quotaPhase).
  if (quotaExhausted) return 'Quota';
  // `step === null` heisst "nichts gemessen offen"; `optimize` UND leer heisst
  // "das Angebot selbst ist leer" — beides ist derselbe Endzustand: ein
  // Angebot ueber eine gemessene Null ist keines.
  if (step === null || (step === 'optimize' && isEmpty)) return 'Done';
  if (step === 'approve') return 'Approve';
  if (step === 'upload') return 'Upload';
  if (step === 'connect') return 'Connect';
  // Schritt 5: der Knopf navigiert NICHT, er klappt Kapitel 3 auf und setzt
  // den Fokus auf den Connect-Knopf (Spec 3.2). Der Ablauf selbst wird nicht
  // in die Karte kopiert -- er lebt im Kapitel und in den Einstellungen.
  if (step === 'ads_connect') return 'AdsConnect';
  return 'Optimize';
}

/**
 * Die schlanke Handlungskarte neben der Score-Karte im Kopf des Feed-Checks.
 *
 * Ersetzt `FeedCheckUpsellCta` am Seitenende (94 Prozent Scrollhoehe, Paket 1
 * der App-Entlastung, T4). Vier Zeilen, genau EIN Primaer-CTA, hoechstens
 * eine Alternative als Textlink — kein zweites Fortschrittsmodell, keine
 * Risikoumkehr-Zeile, kein Druck-Link: die Journey-Punkte im Layout-Band
 * tragen den Fortschritt bereits seit Paket 1, eine fuenfte Fassung davon in
 * einer Karte, die auf EINE Handlung reduziert wird, waere genau die
 * Wiederholung, die dieses Paket abbaut.
 *
 * State-free wie ihre Vorlage: welche Zahl gemessen wird, entscheidet der
 * Aufrufer (`useJourney`, `useFeedCheckOptimize`, `useProductStats`).
 */
export function FeedCheckNextStep({
  step,
  secondaryStep = null,
  actionCount,
  optimizeCount,
  secondaryCount,
  onAction,
  onRun,
  onUploadRun,
  onSecondary,
  onQuota,
  offersOptimizeSecondary = false,
  loading = false,
  uploadBusy = false,
  mcAccessRevoked = false,
  optimizationRunning = false,
  isEmpty = false,
  quotaExhausted = false,
  autoUpload = false,
  uploadElsewhere,
  adsLinked = false,
  adsOwnerManaged = false,
  adsAccountConnected = false,
  adsChapterIndex,
  onInterest,
  interestSent = false,
  interestBusy = false,
  onOpenAds,
  capacityFull = null,
}: FeedCheckNextStepProps) {
  const { t, i18n } = useTranslation('optimization');
  const lang = i18n?.language;

  const mode = deriveMode(step, quotaExhausted, isEmpty);
  // Nur ein anderes Copy-Suffix, kein eigener Modus: Knopf, Ziel und Sperren
  // bleiben die von AdsConnect.
  const copyMode = mode === 'AdsConnect' && adsAccountConnected ? 'AdsAssign' : mode;

  // Nur zwei echte Sperr-Gruende erreichen diese Komponente ueberhaupt: das
  // Kontingent hat bereits vorher den exklusiven Quota-Modus ausgeloest (kein
  // anderer Modus kann `quotaExhausted` gleichzeitig tragen), bleiben Widerruf
  // und laufender Lauf — Widerruf gewinnt.
  const reasonActive = mcAccessRevoked || optimizationRunning;
  const blockedReason = mcAccessRevoked
    ? t('feedCheck.cta.blockedMcRevoked')
    : optimizationRunning
      ? t('feedCheck.cta.blockedRunning')
      : undefined;

  // Der Upload-Knopf HANDELT nur, wenn der Aufrufer `onUploadRun` reicht —
  // fehlt sie, bleibt er reine Navigation (Muster `FeedCheckUpsellCta`), die
  // per Definition nicht scheitern kann und deshalb auch keine Sperre traegt.
  const uploadActs = mode === 'Upload' && !!onUploadRun;

  const primaryDisabled =
    mode === 'Optimize'
      ? loading || mcAccessRevoked || optimizationRunning || isEmpty || quotaExhausted
      : mode === 'Upload'
        ? uploadActs && (uploadBusy || mcAccessRevoked || optimizationRunning)
        : false; // Approve/Connect/Quota: Navigation kann nicht scheitern.

  // Nur die zwei Modi, deren Primaerknopf tatsaechlich handelt (statt zu
  // navigieren), koennen den sichtbaren Grund ueberhaupt ausloesen.
  const primaryReasonVisible = (mode === 'Optimize' || (mode === 'Upload' && uploadActs)) && reasonActive;

  // Alternative: erst die Freigabe-Alternative (Kipp-Regel, `secondaryStep`),
  // sonst — nur in Approve/Upload — das Optimieren-Angebot. Es gibt
  // hoechstens EINEN Alternativlink; zwei waeren drei Angebote in einer Karte.
  type AltLink = { kind: 'approve' | 'optimize'; count: number; onClick?: () => void } | null;
  const altLink: AltLink =
    secondaryStep === 'approve' && (secondaryCount ?? 0) > 0
      ? { kind: 'approve', count: secondaryCount ?? 0, onClick: onSecondary }
      : offersOptimizeSecondary && optimizeCount > 0 && (mode === 'Approve' || mode === 'Upload')
        ? { kind: 'optimize', count: optimizeCount, onClick: onRun }
        : null;

  // Der Freigabe-Link ist reine Navigation und sperrt nie; der Optimieren-Link
  // loest denselben Lauf aus wie der Primaerknopf im Optimieren-Modus und
  // traegt deshalb dieselbe Sperre (ohne Kontingent — das gilt fuer den
  // Optimieren-Primaerknopf, nicht fuer diese Alternative daneben).
  const altDisabled = altLink?.kind === 'optimize' && (loading || mcAccessRevoked || optimizationRunning);
  const altReasonVisible = altLink?.kind === 'optimize' && reasonActive;

  const showReasonBanner = primaryReasonVisible || altReasonVisible;

  // Die Vertrauenszeile ist ein Versprechen ueber die Optimierung ("du
  // pruefst jede Aenderung, bevor sie zu Google geht") — sie darf nicht neben
  // einem Sperr-Grund stehen, der genau das Gegenteil sagt: dass gerade
  // nichts weitergeht.
  const showTrustLine = mode === 'Optimize' && !reasonActive;

  const primaryCount = mode === 'Optimize' ? optimizeCount : actionCount ?? 0;
  const heading =
    mode === 'Connect' || mode === 'Done' || mode === 'Quota' || mode === 'AdsConnect'
      ? t(`feedCheck.nextStep.heading${copyMode}`)
      : t(`feedCheck.nextStep.heading${copyMode}`, {
          count: primaryCount,
          formatted: formatInteger(primaryCount, lang),
        });
  // Der Done-Zustand hat seit Schritt 5 drei Lesarten. Die dritte betrifft
  // Mitglieder eines geteilten Merchant Centers (`adsOwnerManaged`): das
  // Ads-Konto verwaltet der Eigentuemer, der Satz sagt das und bittet um nichts.
  // Die beiden anderen: ist das Ads-Konto
  // verknuepft, stehen die Zahlen im Ads-Kapitel und der Satz sagt das -- mit
  // DER Nummer, die dieses Kapitel auf dieser Seite wirklich traegt. Ohne
  // Verknuepfung kommt dieser Zustand gar nicht mehr vor (`ads_connect` waere
  // dann der naechste Schritt) -- ausser bei leerem Optimieren-Angebot
  // (`isEmpty`), und dort ist der alte Satz weiterhin der richtige.
  // Kennt der Aufrufer die Nummer nicht, faellt der Satz auf den neutralen
  // zurueck: keine Nummer ist besser als eine falsche.
  const reason =
    mode === 'Done' && adsOwnerManaged
      ? t('feedCheck.nextStep.reasonDoneAdsOwner')
      : mode === 'Done' && adsLinked && adsChapterIndex
        ? t('feedCheck.nextStep.reasonDoneAds', { index: adsChapterIndex })
        : t(`feedCheck.nextStep.reason${copyMode}`);

  const primaryOnClick =
    mode === 'Optimize'
      ? onRun
      : mode === 'Upload'
        ? onUploadRun ?? onAction
        // Der Quota-Zweig hat ein eigenes Ziel (/pricing) und nimmt es aus
        // seiner eigenen Prop. Vorher fiel er auf `onAction` durch und traf
        // /pricing nur, weil `useJourney().targets.optimize` bei
        // `quotaBlocked` dorthin umschaltet -- eine Kopplung an eine
        // Ableitung, die dieser Knopf nicht kennt und die niemand pinnt.
        : mode === 'Quota'
          ? onQuota ?? onAction
          : onAction;
  const primaryLabel =
    mode === 'Connect' || mode === 'Quota' || mode === 'AdsConnect'
      ? t(`feedCheck.nextStep.cta${copyMode}`)
      : t(`feedCheck.nextStep.cta${copyMode}`, {
          count: primaryCount,
          formatted: formatInteger(primaryCount, lang),
        });

  const altLabel = altLink
    ? t(`feedCheck.nextStep.alt${altLink.kind === 'approve' ? 'Approve' : 'Optimize'}`, {
        count: altLink.count,
        formatted: formatInteger(altLink.count, lang),
      })
    : '';

  const isBusy = mode === 'Optimize' ? loading : mode === 'Upload' && uploadActs ? uploadBusy : false;
  const PrimaryIcon = mode === 'Optimize' ? Sparkles : ArrowRight;

  return (
    <div
      data-testid="next-step-card"
      className="rounded-2xl border border-primary p-5 sm:p-6 space-y-2 print:hidden"
      style={{ background: 'hsl(var(--primary) / 0.06)', boxShadow: 'var(--shadow-md)' }}
    >
      <p className="text-[11px] font-bold tracking-[0.08em] text-primary uppercase">
        {t('feedCheck.nextStep.eyebrow')}
      </p>
      <h2 className="text-xl sm:text-2xl font-heading font-bold leading-snug">{heading}</h2>
      <p className="text-sm text-muted-foreground">{reason}</p>

      {mode === 'Done' ? (
        // Kein PRIMAERKNOPF: ein Angebot ueber eine gemessene Null ist kein
        // Angebot. Der Testid traegt den Zustand direkt statt ihn aus der
        // Abwesenheit von Buttons zu erschliessen.
        //
        // Seit Schritt 6 (Spec 3.2, D4) steht hier EINE Ausnahme: der Teaser
        // "Interesse melden". Er ist kein naechster Schritt, sondern die
        // einzige Handlung, die es fuer Kampagnen aus Segmenten heute gibt --
        // und ausdruecklich `variant="outline"`, damit die Seite nicht zwei
        // gefuellte Flaechen traegt (Verbot 10 des UX-Audits).
        <div data-testid="next-step-done" className="space-y-2">
          {onInterest && (
            <>
              {/* WP5 A2O-11: der Teaser braucht einen Gegenstand, sonst fragt
                  er nach Interesse an etwas, das nirgends steht -- der Satz
                  nennt Kampagnen aus Anzeigen-Segmenten, der Link fuehrt zu
                  dem Kapitel, das sie liefert. */}
              <p className="text-sm">{t('feedCheck.nextStep.doneTeaser')}</p>
              <div className="flex flex-col items-start gap-1 pt-1 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onInterest}
                  disabled={interestSent || interestBusy}
                  data-testid="next-step-interest"
                  className="h-11 sm:h-9"
                >
                  {interestSent
                    ? t('feedCheck.ads.teaser.reported')
                    : t('feedCheck.ads.teaser.cta')}
                </Button>
                {onOpenAds && (
                  <button
                    type="button"
                    data-testid="next-step-open-ads"
                    onClick={onOpenAds}
                    className="min-h-11 text-sm font-bold text-primary hover:underline sm:min-h-0"
                  >
                    {t('feedCheck.nextStep.openAds')}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-1">
          <Button
            onClick={primaryOnClick}
            disabled={primaryDisabled}
            loading={isBusy}
            variant="default"
            size="lg"
            // `whitespace-normal h-auto py-3`: die Button-Basis traegt
            // `whitespace-nowrap`, und die DE-Copy mit Tausenderzahl
            // ("15.269 Produkte optimieren") lief bei 375px sonst aus der
            // Pill (gemessen an der Vorlage FeedCheckUpsellCta).
            className="whitespace-normal h-auto py-3 text-sm sm:text-base font-bold rounded-md leading-snug"
            title={primaryReasonVisible ? blockedReason : undefined}
            aria-label={primaryReasonVisible ? `${primaryLabel}. ${blockedReason}` : undefined}
          >
            {!isBusy && <PrimaryIcon className="h-4 w-4" aria-hidden="true" />}
            {primaryLabel}
          </Button>

          {altLink && (
            <button
              type="button"
              onClick={altLink.onClick}
              disabled={altDisabled}
              className="text-sm font-medium text-primary hover:underline disabled:pointer-events-none disabled:opacity-50"
              data-testid="next-step-alt"
              title={altReasonVisible ? blockedReason : undefined}
              aria-label={altReasonVisible ? `${altLabel}. ${blockedReason}` : undefined}
            >
              {altLabel}
            </button>
          )}
        </div>
      )}

      {/* WP5 A2O-12: ein volles Kontingent liess das Optimieren-Angebot vorher
          kommentarlos verschwinden. `mode !== 'Quota'` haelt Doppelungen ab --
          der Quota-Zweig fragt bereits ueber die Karte selbst nach mehr
          Platz. */}
      {capacityFull && mode !== 'Quota' && (
        <div
          data-testid="next-step-capacity"
          className="mt-2 flex flex-col gap-1 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm sm:flex-row sm:items-center sm:justify-between"
        >
          <span>
            {capacityFull.kind === 'owner'
              ? t('feedCheck.nextStep.capacityFullOwner', {
                  count: capacityFull.count,
                  formatted: formatInteger(capacityFull.count, lang),
                  email: capacityFull.ownerEmail ?? '',
                })
              : capacityFull.kind === 'contact'
                ? t('feedCheck.nextStep.capacityFullContactHint', {
                    count: capacityFull.count,
                    formatted: formatInteger(capacityFull.count, lang),
                  })
                : t('feedCheck.nextStep.capacityFull', {
                    count: capacityFull.count,
                    formatted: formatInteger(capacityFull.count, lang),
                  })}
          </span>
          {capacityFull.kind === 'upgrade' && onQuota && (
            <button
              type="button"
              onClick={onQuota}
              className="self-start min-h-11 text-sm font-bold text-primary hover:underline sm:min-h-0 sm:self-auto"
            >
              {t('feedCheck.nextStep.capacityFullCta')}
            </button>
          )}
          {capacityFull.kind === 'contact' && (
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              data-testid="next-step-capacity-contact"
              className="self-start inline-flex min-h-11 items-center text-sm font-bold text-primary hover:underline sm:min-h-0 sm:self-auto"
            >
              {t('feedCheck.nextStep.capacityFullContact')}
            </a>
          )}
        </div>
      )}

      {showReasonBanner && (
        <p className="text-xs text-warning-ink" role="status">{blockedReason}</p>
      )}

      {showTrustLine && (
        <p className="text-xs text-muted-foreground">
          {t(autoUpload ? 'feedCheck.cta.noCardAutoUpload' : 'feedCheck.cta.noCard')}
        </p>
      )}

      {mode === 'Upload' && uploadElsewhere && (
        <p className="text-xs text-muted-foreground" role="status">
          {t('feedCheck.cta.uploadRunningElsewhere', {
            uploaded: formatInteger(uploadElsewhere.uploaded, lang),
            total: formatInteger(uploadElsewhere.total, lang),
          })}
        </p>
      )}
    </div>
  );
}
